// @ts-strict-ignore
import { isActionOfType, getActionSubTypePublicId, extractActionOptionIds } from 'flow/service/actions';
import { isChildOfSnippetAction, getRemoteActionPublicId } from 'flow/service/actions/snippets';
import { getDataRoutesForActions, extractDataRouteIds } from 'flow/service/dataRoutes';
import { extractThresholdIds } from 'flow/service/thresholds';
import { ActionType } from 'flow/store/engine/constants';
import { invertPublicIdMap, isPublicId } from 'flow/store/engine/regenerateIds';
import type {
  PublicId,
  Action,
  Threshold,
  DataRoute,
} from 'flow/store/engine/types';
import { isSnippetGateThreshold } from 'flow/store/engine/update/thresholds';
import { getEquivalencyKeyForAction } from '../../collisions/equivalency';
import type { SnippetVersion } from '../../types';


export function buildRemoteToLocalPublicIdMap(args: {
  localSnippetData: SnippetVersion['data'];
  remoteCurrentSnippet: SnippetVersion;
  remoteNextSnippet: SnippetVersion;
  localSnippetActionPublicId: PublicId;
}) {
  const {
    localSnippetData, localSnippetActionPublicId, remoteNextSnippet, remoteCurrentSnippet,
  } = args;

  const currentLocalToRemoteActionIds = buildLocalToRemoteActionPublicIdMap({
    localActions: localSnippetData.actions,
    snippetParentActionPublicId: localSnippetActionPublicId,
    remoteNextActions: remoteNextSnippet.data.actions,
  });

  const currentLocalToRemoteActionSubTypeIds = buildLocalToRemoteActionSubTypePublicIdMap({
    localActions: localSnippetData.actions,
    localToRemoteActionPublicIdMap: currentLocalToRemoteActionIds,
    remoteActions: remoteCurrentSnippet.data.actions,
  });

  const currentLocalToRemoteActionOptionIds = buildLocalToRemoteActionOptionIdMap({
    localActions: localSnippetData.actions,
    localToRemoteActionPublicIdMap: currentLocalToRemoteActionIds,
    remoteActions: remoteCurrentSnippet.data.actions,
  });

  const currentLocalToRemoteThresholdIds = buildLocalToRemoteThresholdPublicIdMap({
    localActions: localSnippetData.actions,
    localThresholds: localSnippetData.thresholds,
    localToRemoteActionPublicIdMap: currentLocalToRemoteActionIds,
    remoteActions: remoteCurrentSnippet.data.actions,
    parentSnippetActionPublicId: localSnippetActionPublicId,
  });

  const currentLocalToRemoteDataRouteIds = buildLocalToRemoteDataRoutePublicIdMap({
    localActions: localSnippetData.actions,
    localDataRoutes: localSnippetData.dataRoutes,
    localToRemoteActionPublicIdMap: currentLocalToRemoteActionIds,
    remoteActions: remoteCurrentSnippet.data.actions,
    remoteDataRoutes: remoteCurrentSnippet.data.dataRoutes,
  });

  // create a map of the public ids between the current and local remote versions
  const currentLocalToRemoteIds = {
    ...currentLocalToRemoteActionIds,
    ...currentLocalToRemoteActionSubTypeIds,
    ...currentLocalToRemoteActionOptionIds,
    ...currentLocalToRemoteThresholdIds,
    ...currentLocalToRemoteDataRouteIds,
  };

  // invert the id - remote to local ids
  const currentRemoteToLocalIds = invertPublicIdMap(currentLocalToRemoteIds);

  return currentRemoteToLocalIds;
}


export function buildLocalToRemoteActionPublicIdMap(args: {
  localActions: Record<PublicId, Action>;
  remoteNextActions: Record<PublicId, Action>;
  snippetParentActionPublicId: PublicId;
}): Record<PublicId, PublicId> {
  const { localActions, remoteNextActions, snippetParentActionPublicId } = args;

  // id map of all local child action ids to their remote action ids for the given snippet parent
  const localToRemoteActionPublicIdMap = Object.fromEntries(
    Object.values(localActions).flatMap(localAction => {
      if (!isChildOfSnippetAction({ action: localAction, snippetParentActionPublicId })) {
        return [];
      }

      const localActionPublicId = localAction.publicId;
      const remoteActionPublicId = getRemoteActionPublicId({
        action: localAction,
        snippetParentActionPublicId,
      });

      const remoteNextAction = remoteNextActions[remoteActionPublicId];

      // for inputs, we need to compare the current and next action and omit grabbing it's remote id if
      // they are no longer equivalent
      if (remoteNextAction && isActionOfType(localAction, ActionType.INPUT)) {
        const localEqKey = getEquivalencyKeyForAction({ action: localAction });
        const remoteEqKey = getEquivalencyKeyForAction({ action: remoteNextAction });

        if (localEqKey !== remoteEqKey) {
          return [];
        }
      }

      return [[localActionPublicId, remoteActionPublicId]];
    }),
  );

  return localToRemoteActionPublicIdMap;
}


export function buildLocalToRemoteActionSubTypePublicIdMap(args: {
  localToRemoteActionPublicIdMap: Record<PublicId, PublicId>;
  localActions: Record<PublicId, Action>;
  remoteActions: Record<PublicId, Action>;
}): Record<PublicId, PublicId> {
  const {
    localToRemoteActionPublicIdMap, localActions, remoteActions,
  } = args;

  const localToRemoteEntries = Object.entries(localToRemoteActionPublicIdMap)
    .flatMap(([localActionId, remoteActionId]) => {
      const localAction = localActions[localActionId];
      const remoteAction = remoteActions[remoteActionId];

      const localActionSubTypePublicId = getActionSubTypePublicId(localAction);
      const remoteActionSubTypePublicId = getActionSubTypePublicId(remoteAction);

      if (!localActionSubTypePublicId || !remoteActionSubTypePublicId) {
        return [];
      }
      return [[localActionSubTypePublicId, remoteActionSubTypePublicId]];
    });

  return Object.fromEntries(localToRemoteEntries);
}


export function buildLocalToRemoteActionOptionIdMap(args: {
  localToRemoteActionPublicIdMap: Record<PublicId, PublicId>;
  localActions: Record<PublicId, Action>;
  remoteActions: Record<PublicId, Action>;
}): Record<PublicId, PublicId> {
  const {
    localToRemoteActionPublicIdMap, localActions, remoteActions,
  } = args;

  const localToRemoteEntries = Object.entries(localToRemoteActionPublicIdMap)
    .flatMap(([localActionId, remoteActionId]) => {
      const localAction = localActions[localActionId];
      const remoteAction = remoteActions[remoteActionId];

      const localActionOptionIds = extractActionOptionIds(localAction);
      const remoteActionOptionIds = extractActionOptionIds(remoteAction);

      if (localActionOptionIds.length !== remoteActionOptionIds.length) {
        throw new Error('action option count must be equal across local and remote actions');
      }

      return Array.from({ length: localActionOptionIds.length }, (_, idx) => {
        const localOptionId = localActionOptionIds[idx];
        const remoteOptionId = remoteActionOptionIds[idx];

        return [localOptionId, remoteOptionId];
      });
    });

  return Object.fromEntries(localToRemoteEntries);
}


export function buildLocalToRemoteThresholdPublicIdMap(args: {
  localToRemoteActionPublicIdMap: Record<PublicId, PublicId>;
  localActions: Record<PublicId, Action>;
  localThresholds: Record<PublicId, Threshold>;
  remoteActions: Record<PublicId, Action>;
  parentSnippetActionPublicId: PublicId;
}): Record<PublicId, PublicId> {
  const {
    localToRemoteActionPublicIdMap,
    localActions,
    localThresholds,
    remoteActions,
    parentSnippetActionPublicId,
  } = args;


  const localToRemoteEntries = Object.entries(localToRemoteActionPublicIdMap)
    .flatMap(([localActionId, remoteActionId]) => {
      const localAction = localActions[localActionId];
      const remoteAction = remoteActions[remoteActionId];

      const localThresholdIds = extractThresholdIds(localAction.rules)
        .filter(id => {
          const threshold = localThresholds[id];
          return !threshold || !isSnippetGateThreshold({
            threshold,
            parentSnippetActionPublicId,
          });
        });
      const remoteThresholdIds = extractThresholdIds(remoteAction.rules);

      if (localThresholdIds.length !== remoteThresholdIds.length) {
        throw new Error('threshold count must be equal across local and remote rules');
      }

      return Array.from({ length: localThresholdIds.length }, (_, idx) => {
        const localThresholdId = localThresholdIds[idx];
        const remoteThresholdId = remoteThresholdIds[idx];

        return [localThresholdId, remoteThresholdId];
      });
    });

  return Object.fromEntries(localToRemoteEntries);
}


export function buildLocalToRemoteDataRoutePublicIdMap(args: {
  localActions: Record<PublicId, Action>;
  localDataRoutes: Record<PublicId, DataRoute>;
  localToRemoteActionPublicIdMap: Record<PublicId, PublicId>;
  remoteActions: Record<PublicId, Action>;
  remoteDataRoutes: Record<PublicId, DataRoute>;
}): Record<PublicId, PublicId> {
  const {
    localActions, localDataRoutes, localToRemoteActionPublicIdMap, remoteActions, remoteDataRoutes,
  } = args;

  const localToRemoteEntries = Object.entries(localToRemoteActionPublicIdMap)
    .flatMap(([localActionId, remoteActionId]) => {
      const localAction = localActions[localActionId];
      const remoteAction = remoteActions[remoteActionId];

      if (localAction.type !== remoteAction.type) {
        throw new Error('local and remote action types must match');
      }

      switch (localAction.type) {
        case ActionType.MATRIX_MODEL:
          return getLocalToRemoteDataRoutePublicIdForMatrixAction({
            localAction,
            remoteAction,
          });

        case ActionType.OUTPUT:
          return getLocalToRemoteDataRoutePublicIdForOutputAction({
            localAction,
            remoteAction,
          });

        case ActionType.SERVICE:
          return getLocalToRemoteDataRoutePublicIdsForServiceAction({
            localAction,
            localDataRoutes,
            localToRemoteActionPublicIdMap,
            remoteAction,
            remoteDataRoutes,
          });

        default:
          return [];
      }
    });

  return Object.fromEntries(localToRemoteEntries);
}


function getLocalToRemoteDataRoutePublicIdsForServiceAction(args: {
  localDataRoutes: Record<PublicId, DataRoute>;
  remoteDataRoutes: Record<PublicId, DataRoute>;
  localAction: Action;
  remoteAction: Action;
  localToRemoteActionPublicIdMap: Record<PublicId, PublicId>;
}): Array<[PublicId, PublicId]> {
  const {
    localAction, localDataRoutes, localToRemoteActionPublicIdMap, remoteAction, remoteDataRoutes,
  } = args;


  const localDataRoutesPointingToLocalAction = getDataRoutesForActions({
    action: localAction,
    dataRoutes: localDataRoutes,
  });


  const remoteDataRoutesPointingToRemoteAction = getDataRoutesForActions({
    action: remoteAction,
    dataRoutes: remoteDataRoutes,
  });


  if (localDataRoutesPointingToLocalAction.length
    !== remoteDataRoutesPointingToRemoteAction.length) {
    throw new Error('length of local and remote data routes id must be the same');
  }

  return Array.from({ length: localDataRoutesPointingToLocalAction.length }, (_, idx) => {
    const localDataRoute = localDataRoutesPointingToLocalAction[idx];

    const remoteSourceIdForLocal = localToRemoteActionPublicIdMap[localDataRoute.sourceId];
    const remoteTargetIdForLocal = localToRemoteActionPublicIdMap[localDataRoute.targetId];

    // not expected to change between local and remote, so direct access is enough
    const remoteCategoryNameForLocalCategoryName = localDataRoute.category.name;

    const remoteDataRoute = remoteDataRoutesPointingToRemoteAction.find(dataRoute => (
      dataRoute.sourceId === remoteSourceIdForLocal
      && dataRoute.targetId === remoteTargetIdForLocal
      && dataRoute.category.name === remoteCategoryNameForLocalCategoryName
    ));

    if (!remoteDataRoute) {
      throw new Error('could not find remote data route for local data route');
    }

    return [localDataRoute.publicId, remoteDataRoute.publicId];
  });
}


function getLocalToRemoteDataRoutePublicIdForOutputAction(args: {
  localAction: Action;
  remoteAction: Action;
}): Array<[PublicId, PublicId]> {
  const { localAction, remoteAction } = args;

  if (localAction.type !== remoteAction.type) {
    throw new Error('local and remote actions must have the same type');
  }

  const localExpression = localAction.output.expression;
  const remoteExpression = remoteAction.output.expression;

  if (localExpression.length !== remoteExpression.length) {
    throw new Error('local and remote expression lengths must be the same');
  }

  const localExpressionDataRouteIds = extractDataRouteIds(localExpression);
  const remoteExpressionDataRouteIds = extractDataRouteIds(remoteExpression);


  if (localExpressionDataRouteIds.length !== remoteExpressionDataRouteIds.length) {
    throw new Error('local and remote expression data route ids must be the same');
  }

  return Array.from({ length: localExpressionDataRouteIds.length }, (_, idx) => {
    const localDataRouteId = localExpressionDataRouteIds[idx];
    const remoteDataRouteId = remoteExpressionDataRouteIds[idx];

    return [localDataRouteId, remoteDataRouteId];
  });
}


function getLocalToRemoteDataRoutePublicIdForMatrixAction(args: {
  localAction: Action;
  remoteAction: Action;
}): Array<[PublicId, PublicId]> {
  const { localAction, remoteAction } = args;

  if (localAction.type !== remoteAction.type) {
    throw new Error('local and remote actions must have the same type');
  }

  const localDataRouteXPublicId = localAction.matrixModel.dataRouteX;
  const remoteDataRouteXPublicId = remoteAction.matrixModel.dataRouteX;

  const localDataRouteYPublicId = localAction.matrixModel.dataRouteY;
  const remoteDataRouteYPublicId = remoteAction.matrixModel.dataRouteY;

  const hasLocalDataRouteXPublicId = isPublicId(localDataRouteXPublicId);
  const hasRemoteDataRouteXPublicId = isPublicId(remoteDataRouteXPublicId);

  if (hasLocalDataRouteXPublicId !== hasRemoteDataRouteXPublicId) {
    throw new Error('local and remote data route X existence mismatch');
  }

  const hasLocalDataRouteYPublicId = isPublicId(localDataRouteYPublicId);
  const hasRemoteDataRouteYPublicId = isPublicId(remoteDataRouteYPublicId);

  if (hasLocalDataRouteYPublicId !== hasRemoteDataRouteYPublicId) {
    throw new Error('local and remote data route Y existence mismatch');
  }

  const hasDataRouteXPublicId = hasLocalDataRouteXPublicId && hasRemoteDataRouteXPublicId;
  const hasDataRouteYPublicId = hasLocalDataRouteYPublicId && hasRemoteDataRouteYPublicId;

  return [
    ...(hasDataRouteXPublicId ? [[localDataRouteXPublicId, remoteDataRouteXPublicId]] : []),
    ...(hasDataRouteYPublicId ? [[localDataRouteYPublicId, remoteDataRouteYPublicId]] : []),
  ] as Array<[PublicId, PublicId]>;
}
